import Util from "packs/utils/Util"
import { funcName, Logger } from "packs/common"

export default class FormUtil {
    private static waaqParamsListenerAttached = false
    private static completionRedirectHandled = false
    private static readonly WAAQ_PARAM_KEYS = new Set([
        "marketing_key",
        "visitor_key",
        "session_key",
        "access_event_key",
        "handoff_id",
        "launch_event_id",
        "submission_id",
        "external_event_id",
        "landing_page",
        "page_url",
        "page_path",
        "page_title",
        "referrer",
        "referrer_url",
        "referrer_host",
        "original_referrer",
        "current_page_url",
        "current_page_path",
        "current_referrer",
        "utm_source",
        "utm_medium",
        "utm_campaign",
        "utm_term",
        "utm_content",
        "source_type",
        "is_iframe",
        "parent_page_url",
        "iframe_src",
        "accessed_at",
        "occurred_at",
        "device_type",
        "is_mobile",
        "screen_width",
        "screen_height",
        "timezone",
        "sdk_version",
        "trigger_type",
        "trigger_provider",
        "launch_mode",
        "public_room_id",
    ])
    private static readonly DANGEROUS_KEYS = new Set(["__proto__", "prototype", "constructor"])
    private static readonly MAX_WAAQ_PARAMS_BYTES = 16 * 1024

    static getParams(): any {
        let params = {}
        let _params = window.location.search.substr(1).split("&")
        for (let _param of _params) {
            if (!_param) continue

            let [key, value] = _param.split("=").map(decodeURIComponent)

            if (!key || key.length > 200) continue
            if (key === "waaq_handoff") continue

            // ネストしたパラメータの処理（例: attendees[0][name]）
            if (key.includes("[") && key.includes("]")) {
                const matches = key.match(/([^\[]+)(\[.+)/)
                if (matches) {
                    const mainKey = matches[1]
                    const remainingKeys = matches[2]

                    // ネストしたキーを解析
                    const keyParts = [mainKey]
                    const nestedMatches = remainingKeys.match(/\[([^\]]*)\]/g)
                    if (nestedMatches) {
                        nestedMatches.forEach(match => {
                            keyParts.push(match.slice(1, -1))
                        })
                    }
                    if (keyParts.some(part => this.DANGEROUS_KEYS.has(part))) continue

                    // ネストした構造を作成
                    let current = params
                    for (let i = 0; i < keyParts.length; i++) {
                        const part = keyParts[i]

                        if (i === keyParts.length - 1) {
                            // 最後のキーに値を設定
                            current[part] = value
                        } else {
                            // 中間のオブジェクトを作成
                            if (!current[part]) {
                                // 次のキーが数字なら配列、そうでなければオブジェクト
                                const nextIsArray = !isNaN(Number(keyParts[i + 1]))
                                current[part] = nextIsArray ? [] : {}
                            }
                            current = current[part]
                        }
                    }
                }
            } else {
                // 通常のパラメータ
                if (this.DANGEROUS_KEYS.has(key)) continue
                params[key] = value
            }
        }

        // 流入元情報を追加
        try {
            params = this.resolveMarketingContext(this.addTrackingInfo(params))
        } catch (e) {
            Logger(`${funcName()} 流入元情報の追加に失敗しました: ${e}`)
        }

        // this.additionalParams = params
        Logger(`パラメータを取得しました: string:${_params} ${Util.output(params)}`)
        return params
    }

    static addTrackingInfo(params: any) {
        // UTMパラメータの明示的な取得
        const utmParams = this.getUtmParams()

        // Referrer情報の取得
        const referrerInfo = this.getReferrerInfo()

        // iframe埋め込み判定と親ページ情報取得
        const isInIframe = window.self !== window.top
        const parentInfo = this.getParentPageInfo()

        // 匿名ID・訪問回数
        const identityParams = this.getAnonymousIdentityParams()
        const visitParams = this.getVisitParams()

        // その他の流入元情報
        const trackingInfo = {
            // UTMパラメータ
            ...utmParams,

            // Referrer情報
            referrer_url: referrerInfo.referrer,
            referrer_host: referrerInfo.host,
            referrer_is_external: referrerInfo.is_external,

            // その他の有用な情報

            accessed_at: new Date().getTime() / 1000,
            user_agent: navigator.userAgent,
            language: navigator.language,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,

            screen_resolution: `${screen.width}x${screen.height}`,
            screen_width: screen.width,
            screen_height: screen.height,

            viewport_width: window.innerWidth,
            viewport_height: window.innerHeight,

            is_mobile: /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent),
            device_type: this.getDeviceType(),
            source_type: "frontend",

            // iframe埋め込み判定と親ページ情報
            is_iframe: isInIframe,
            parent_page_url: parentInfo.parentUrl,
            parent_page_host: parentInfo.parentHost,
            iframe_src: isInIframe ? window.location.href : null,
            landing_page: window.location.pathname,
            page_url: window.location.href,
            original_referrer: referrerInfo.referrer,
            current_page_url: window.location.href,
            current_page_path: window.location.pathname,
            current_referrer: referrerInfo.referrer,

            // 将来AI/ルール出し分け時に使う。URLパラメータ側にあれば params が優先される。
            variant_id: null,
            trigger_rule_id: null,
            first_layer_pattern_id: null,
            policy_version: null,
            model_version: null,
            decision_id: null,
        }

        // nullや空文字列を除外してクリーンにする
        const cleanTrackingInfo = Object.fromEntries(
            Object.entries(trackingInfo).filter(([_, value]) => value !== null && value !== "")
        )

        // 既存のparamsに追加（既存のパラメータを優先）
        return { ...cleanTrackingInfo, ...params }
    }

    /**
     * Marketing identity precedence:
     * server handoff > trusted postMessage > auto.js init object > WAAQ
     * session storage > legacy/query waaq_params. Sources with a conflicting
     * marketing_key are never merged into the selected campaign context.
     */
    static resolveMarketingContext(legacyParams: Record<string, unknown>): Record<string, unknown> {
        const runtimeWindow = window as any
        const candidates: unknown[] = [
            runtimeWindow.__WaaqMarketingHandoff,
            this.getWaaqParamsFromParent(),
            runtimeWindow.__WaaqMarketingInit,
            this.readStoredMarketingContext(),
            legacyParams,
        ]
        const sources = candidates.flatMap(candidate => {
            if (!this.isPlainObject(candidate)) return []
            const safe = this.sanitizeWaaqParamsPayload(candidate)
            return this.isValidWaaqParamsPayload(safe) ? [safe] : []
        })
        const selectedMarketingKey = sources.find(source => source.marketing_key)?.marketing_key
        const resolved = Object.create(null)

        sources.forEach(source => {
            if (selectedMarketingKey && source.marketing_key && source.marketing_key !== selectedMarketingKey) return
            Object.entries(source).forEach(([key, value]) => {
                if (resolved[key] === undefined || resolved[key] === null || resolved[key] === "") resolved[key] = value
            })
        })

        resolved.current_page_url = window.location.href
        resolved.current_page_path = window.location.pathname
        resolved.current_referrer = document.referrer || null
        resolved.original_referrer ||= resolved.referrer || resolved.referrer_url || document.referrer || null
        this.storeMarketingContext(resolved)

        // Non-marketing query parameters still drive existing WAAQ forms.
        return { ...legacyParams, ...resolved }
    }

    private static readStoredMarketingContext(): Record<string, unknown> | null {
        try {
            const stored = sessionStorage.getItem("waaq_marketing_context")
            const parsed = stored ? JSON.parse(stored) : null
            return this.isValidWaaqParamsPayload(parsed) ? parsed : null
        } catch (_) {
            return null
        }
    }

    private static storeMarketingContext(value: Record<string, unknown>): void {
        try {
            const safe = Object.fromEntries(
                Object.entries(value).filter(([, item]) => item !== null && item !== undefined && item !== "")
            )
            if (this.isValidWaaqParamsPayload(safe)) {
                sessionStorage.setItem("waaq_marketing_context", JSON.stringify(this.sanitizeWaaqParamsPayload(safe)))
            }
        } catch (_) {}
    }

    // UTMパラメータの取得
    static getUtmParams(): any {
        const urlParams = new URLSearchParams(window.location.search)
        return {
            utm_source: urlParams.get("utm_source"),
            utm_medium: urlParams.get("utm_medium"),
            utm_campaign: urlParams.get("utm_campaign"),
            utm_term: urlParams.get("utm_term"),
            utm_content: urlParams.get("utm_content"),
        }
    }

    // Referrer情報の取得
    static getReferrerInfo(): any {
        const referrer = document.referrer
        let host = null

        if (referrer) {
            try {
                const url = new URL(referrer)
                host = url.hostname
            } catch (e) {
                // URL解析エラーの場合
            }
        }

        return {
            referrer: referrer || null,
            host: host,
            is_external: host && host !== window.location.hostname,
        }
    }

    // 親ページ情報取得メソッド
    static getParentPageInfo(): any {
        const isInIframe = window.self !== window.top
        let parentUrl = null
        let parentHost = null

        if (isInIframe) {
            // 1. URLパラメータから取得（最優先）
            const urlParams = new URLSearchParams(window.location.search)
            if (urlParams.get("parent_url")) {
                parentUrl = urlParams.get("parent_url")
                try {
                    parentHost = new URL(parentUrl).hostname
                } catch (e) {}
            }
        }

        return {
            parentUrl,
            parentHost,
            isInIframe,
        }
    }

    // waaq_params を受信するリスナー
    static setupWaaqParamsListener() {
        Logger(`FormUtil.setupWaaqParamsListener`)
        if (this.waaqParamsListenerAttached) return

        this.waaqParamsListenerAttached = true
        window.addEventListener("message", function (event) {
            if (
                event.data?.type === "waaq_params" &&
                FormUtil.isTrustedWaaqParamsMessage(event) &&
                FormUtil.isValidWaaqParamsPayload(event.data.params)
            ) {
                // 受信したパラメータを保存
                const safeParams = FormUtil.sanitizeWaaqParamsPayload(event.data.params)
                sessionStorage.setItem("waaq_params_from_parent", JSON.stringify(safeParams))
                FormUtil.storeMarketingContext(safeParams)
                Logger(`FormUtil.setupWaaqParamsListener Received waaq_params from parent`)
                window.dispatchEvent(new CustomEvent("waaq:params", { detail: safeParams }))
            }
        })

        const parentOrigin = this.expectedParentOrigin()
        if (window.parent !== window && parentOrigin) {
            window.parent.postMessage({ type: "waaq_params_ready" }, parentOrigin)
        }
    }

    static isTrustedWaaqParamsMessage(event: MessageEvent): boolean {
        if (!event || event.source !== window.parent || !event.origin || event.origin === "null") return false
        if (!this.isPlainObject(event.data) || event.data.type !== "waaq_params") return false

        const expectedOrigin = this.expectedParentOrigin()
        return expectedOrigin !== null && event.origin === expectedOrigin
    }

    static expectedParentOrigin(): string | null {
        const parentUrl = new URLSearchParams(window.location.search).get("parent_url")
        const referrer = document.referrer
        const parentOrigin = parentUrl ? this.safeParentOrigin(parentUrl) : null
        const referrerOrigin = referrer ? this.safeParentOrigin(referrer) : null

        // A supplied but invalid source, or two authoritative sources that
        // disagree, is ambiguous and therefore rejected.
        if ((parentUrl && !parentOrigin) || (referrer && !referrerOrigin)) return null
        if (parentOrigin && referrerOrigin && parentOrigin !== referrerOrigin) return null
        return parentOrigin || referrerOrigin
    }

    /**
     * 完了後URLへ遷移します。
     *
     * auto.js の iframe 内では外部ページを iframe 自身に読み込まず、
     * 検証済みの親 origin へトップページ遷移を依頼します。
     */
    static redirectAfterCompletion(url: string): boolean {
        const resolvedUrl = Util.getUrl(url)
        if (!resolvedUrl) return false
        if (this.completionRedirectHandled) return true

        if (this.isInIframe()) {
            const parentOrigin = this.expectedParentOrigin()
            if (!parentOrigin) return false

            try {
                window.parent.postMessage({ type: "waaq_redirect", url: resolvedUrl }, parentOrigin)
                this.completionRedirectHandled = true
                return true
            } catch (e) {
                Logger(`${funcName()} 親ページへのリダイレクト通知に失敗しました: ${e}`)
                return false
            }
        }

        this.completionRedirectHandled = true
        this.assignCurrentPage(resolvedUrl)
        return true
    }

    static isInIframe(): boolean {
        return window.self !== window.top
    }

    private static assignCurrentPage(url: string): void {
        window.location.assign(url)
    }

    static isValidWaaqParamsPayload(value: unknown): value is Record<string, unknown> {
        if (!this.isPlainObject(value)) return false
        const entries = Object.entries(value)
        if (entries.length > 80) return false
        let payloadBytes = 0

        return entries.every(([key, item]) => {
            if (!this.WAAQ_PARAM_KEYS.has(key) || this.DANGEROUS_KEYS.has(key) || key.length > 100) return false
            if (!["string", "number", "boolean"].includes(typeof item)) return false
            if (typeof item === "number" && !Number.isFinite(item)) return false
            if (typeof item === "string" && item.length > 2000) return false
            payloadBytes += key.length + (typeof item === "string" ? item.length : `${item}`.length)
            return payloadBytes <= this.MAX_WAAQ_PARAMS_BYTES
        })
    }

    static sanitizeWaaqParamsPayload(value: Record<string, unknown>): Record<string, unknown> {
        const safe = Object.create(null)
        Object.entries(value).forEach(([key, item]) => {
            if (!this.WAAQ_PARAM_KEYS.has(key) || this.DANGEROUS_KEYS.has(key)) return
            safe[key] = typeof item === "string" ? item.slice(0, 2000) : item
        })
        return safe
    }

    private static safeParentOrigin(value: string, childProtocol = window.location.protocol): string | null {
        try {
            const url = new URL(value, window.location.href)
            if (!["http:", "https:"].includes(url.protocol) || url.origin === "null") return null
            if (childProtocol === "https:" && url.protocol !== "https:") return null
            if (url.username || url.password) return null
            return url.origin
        } catch (e) {
            return null
        }
    }

    private static isPlainObject(value: unknown): value is Record<string, any> {
        if (!value || typeof value !== "object" || Array.isArray(value)) return false
        const prototype = Object.getPrototypeOf(value)
        return prototype === Object.prototype || prototype === null
    }

    // 親から受信したwaaq_paramsを取得
    static getWaaqParamsFromParent(): any {
        try {
            const stored = sessionStorage.getItem("waaq_params_from_parent")
            return stored ? JSON.parse(stored) : null
        } catch (e) {
            return null
        }
    }

    static getOrCreateSubmissionId(scope: string): string {
        const storageKey = this.submissionStorageKey(scope)
        const existing = sessionStorage.getItem(storageKey)
        if (existing) return existing

        const random =
            typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
                ? crypto.randomUUID()
                : `${Date.now()}_${Math.random().toString(36).slice(2)}`
        const submissionId = `sub_${random}`
        sessionStorage.setItem(storageKey, submissionId)
        return submissionId
    }

    static clearSubmissionId(scope: string, completedSubmissionId?: string): void {
        const storageKey = this.submissionStorageKey(scope)
        if (completedSubmissionId && sessionStorage.getItem(storageKey) !== completedSubmissionId) return
        sessionStorage.removeItem(storageKey)
    }

    private static submissionStorageKey(scope: string): string {
        const safeScope = `${scope || "form"}`.replace(/[^a-zA-Z0-9_-]/g, "_").slice(0, 100)
        return `waaq_submission_id:${safeScope}`
    }

    static validateTel(tel): boolean {
        if (Util.isBlank(tel)) return false

        Logger(`${funcName()} tel:${tel}`)
        let telStr = tel.toString()

        telStr = telStr.replace(/[Ａ-Ｚａ-ｚ０-９]/g, function (s) {
            return String.fromCharCode(s.charCodeAt(0) - 65248)
        })
        let isValid = false

        if (telStr.length > 5 && telStr.match(/^(?:\+?\d+-)?\d+(?:-\d+){2}$|^\+?\d+$/)) {
            isValid = true
        }
        return isValid
    }

    /**
     * 6文字の乱数を自動で生成します.
     * @param len
     * @returns
     */
    static generateRamdom(len = 6) {
        return Array(len)
            .fill(null)
            .map(() => Math.random().toString(36)[2])
            .join("")
            .toUpperCase()
    }

    static getAnonymousIdentityParams(): any {
        const visitorKey = this.getOrCreateVisitorKey()
        const sessionKey = this.getOrCreateSessionKey()

        return {
            visitor_key: visitorKey,
            session_key: sessionKey,

            // DailyStatAccessKey の unique 判定用
            // まずは visitor_key と同じでOK
            unique_key: visitorKey,
        }
    }

    static getOrCreateVisitorKey(): string {
        const key = "waaq_visitor_key"

        try {
            let visitorKey = localStorage.getItem(key)
            if (visitorKey) return visitorKey
            visitorKey = `v_${Date.now()}_${this.generateRamdom(16)}`
            localStorage.setItem(key, visitorKey)

            return visitorKey
        } catch (e) {
            return `v_tmp_${Date.now()}_${this.generateRamdom(16)}`
        }
    }

    static getOrCreateSessionKey(): string {
        const key = "waaq_session_key"

        try {
            let sessionKey = sessionStorage.getItem(key)
            if (sessionKey) return sessionKey
            sessionKey = `s_${Date.now()}_${this.generateRamdom(16)}`
            sessionStorage.setItem(key, sessionKey)
            return sessionKey
        } catch (e) {
            return `s_tmp_${Date.now()}_${this.generateRamdom(16)}`
        }
    }

    static getVisitParams(): any {
        const visitCountKey = "waaq_visit_count"
        const sessionCountedKey = "waaq_session_visit_counted"

        try {
            const alreadyCounted = sessionStorage.getItem(sessionCountedKey) === "1"

            let current = Number(localStorage.getItem(visitCountKey) || "0")

            if (!alreadyCounted) {
                current += 1
                localStorage.setItem(visitCountKey, String(current))
                sessionStorage.setItem(sessionCountedKey, "1")
            }

            return {
                visit_count: current,
                is_returning_visitor: current > 1,
            }
        } catch (e) {
            return {
                visit_count: null,
                is_returning_visitor: null,
            }
        }
    }

    static getDeviceType(): string {
        const ua = navigator.userAgent

        if (/iPad|Tablet|Android(?!.*Mobile)/i.test(ua)) {
            return "tablet"
        }

        if (/Android|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua)) {
            return "sp"
        }

        return "pc"
    }
}
